import { createClient } from '@supabase/supabase-js'

/**
 * Supabase client con autodetección de configuración real vs placeholder.
 *
 * En v1.1.x, mientras B8 (Supabase production project) no exista, la app
 * corre en MODO DEMO: el cliente sigue existiendo (para no romper imports)
 * pero `IS_SUPABASE_CONFIGURED` es false y los callsites de auth lo
 * detectan y caen al flujo demo (sessionStorage).
 *
 * Cuando founder cree el proyecto hosted y reemplace las env vars, este
 * módulo automáticamente devolverá un cliente real funcional — cero
 * cambios de código necesarios.
 */

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || ''
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || ''

/**
 * COLA:el-front-llama-placeholder-a-localhost · UN BACKEND LOCAL NO ES UN PLACEHOLDER.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * Orden del founder (2026-10-18): «localhost/127.0.0.1 **no son placeholder** si la env declara
 * backend local; configurado = URL https de Supabase **o http local declarado** + clave con forma».
 *
 * 🔴 LO QUE PASABA, medido por REALIDAD en el entorno de cata local recién montado: `localhost` y
 * `127.0.0.1` estaban en la lista de PLACEHOLDER, y además se exigía `^https://*.supabase.co`. Con
 * el stack local en marcha, `IS_SUPABASE_CONFIGURED` salía **false**, la app caía en MODO DEMO y
 * **el login era imposible por pantalla**: David metía su correo y le salía un modal de «alpha
 * cerrada». El entorno existía y no se podía catar nada por donde cata un usuario.
 *
 * Y lo que lo dejó pasar es mío: `scripts/cata/comprobar.js` daba el criterio EN VERDE porque
 * atacaba la API con `fetch`, **saltándose el cliente del front**. Un arnés que no pasa por donde
 * pasa el usuario no mide al usuario. Por eso esa comprobación ahora entra por el navegador.
 *
 * LA REGLA, en una frase: está configurado cuando la URL es un backend **reconocible** —el hosted
 * de Supabase por https, o un localhost declarado— **y** la clave tiene forma de clave.
 */

// Lo que sigue siendo placeholder: un hueco sin rellenar. Localhost NO está aquí: localhost es una
// decisión de quien escribió la env, no un hueco.
const PLACEHOLDER_PATTERNS = [
  /placeholder\.supabase/i,
  /your[-_]?supabase/i,
  /example\.supabase/i,
  /xyzcompany\.supabase/i,
  /project-id\.supabase/i,
  /<.*>/,                       // «https://<tu-proyecto>.supabase.co» y familia
]

/** El hosted de Supabase, por https y con su subdominio. */
const ES_SUPABASE_HOSTED = (url) => /^https:\/\/[a-z0-9-]+\.supabase\.co(?::\d+)?(?:\/|$)/i.test(url)

/**
 * Un backend local DECLARADO: la env apunta a un origen local. Se acepta http —en local no hay
 * TLS— y también https por si alguien levanta el stack con certificado.
 * Nada de adivinar: si la env dice `http://127.0.0.1:54321`, eso es una declaración.
 */
const ES_BACKEND_LOCAL = (url) =>
  /^https?:\/\/(?:127\.0\.0\.1|localhost|\[::1\])(?::\d+)?(?:\/|$)/i.test(url)

/**
 * Clave CON FORMA, no clave larga. `key.length < 40` dejaba pasar cualquier cadena de 40 caracteres
 * y rechazaba formatos nuevos. Las dos formas que Supabase emite hoy:
 *   · JWT (`eyJ...` con tres segmentos separados por punto) — la anon de siempre, hosted y local;
 *   · `sb_publishable_…` — el formato nuevo del CLI y del dashboard.
 */
function claveConForma(key) {
  if (typeof key !== 'string') return false
  const k = key.trim()
  if (/^sb_publishable_[A-Za-z0-9_-]{10,}$/.test(k)) return true
  const partes = k.split('.')
  return k.startsWith('eyJ') && partes.length === 3 && partes.every((p) => p.length >= 8)
}

function detectConfigured(url, key) {
  if (!url || !key) return false
  if (PLACEHOLDER_PATTERNS.some((p) => p.test(url))) return false
  if (!claveConForma(key)) return false
  return ES_SUPABASE_HOSTED(url) || ES_BACKEND_LOCAL(url)
}

export const IS_SUPABASE_CONFIGURED = detectConfigured(supabaseUrl, supabaseAnonKey)

/** La razón por la que NO está configurado, para que la pantalla diga la verdad y no «alpha cerrada». */
export const PORQUE_NO_CONFIGURADO = IS_SUPABASE_CONFIGURED
  ? null
  : (!supabaseUrl || !supabaseAnonKey)
    ? 'falta_env'
    : PLACEHOLDER_PATTERNS.some((p) => p.test(supabaseUrl))
      ? 'url_placeholder'
      : !claveConForma(supabaseAnonKey)
        ? 'clave_sin_forma'
        : 'url_no_reconocida'

export const ES_BACKEND_LOCAL_DECLARADO = ES_BACKEND_LOCAL(supabaseUrl)
export { detectConfigured, claveConForma, ES_BACKEND_LOCAL, ES_SUPABASE_HOSTED }

// Para el cliente: si está configurado, real. Si no, stub que no rompe
// imports pero falla limpio si alguien intenta usarlo (los callsites de
// auth checkean IS_SUPABASE_CONFIGURED antes).
function createStub() {
  const err = () =>
    Promise.reject(
      new Error('Supabase not configured — running in demo mode. Set REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY to enable real auth.')
    )
  const ch = () => ({ subscribe: () => ({ unsubscribe: () => {} }) })
  return {
    auth: {
      signInWithPassword: err,
      signUp: err,
      signOut: () => Promise.resolve(),
      getSession: () => Promise.resolve({ data: { session: null } }),
      getUser: () => Promise.resolve({ data: { user: null } }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
      resetPasswordForEmail: err,
    },
    from: () => ({
      select: () => ({ eq: () => ({ single: () => Promise.resolve({ data: null, error: null }) }) }),
      insert: err,
      update: () => ({ eq: () => ({ select: () => ({ single: err }) }) }),
      upsert: err,
      delete: () => ({ eq: err }),
    }),
    channel: ch,
    removeChannel: () => {},
  }
}

// COLA:el-refresco-no-insiste · el `fetch` que le pone la N al refresco de sesión. Va aquí y no en un `useEffect` porque el bucle ocurre **dentro** de la
// librería (`_refreshAccessToken` con su `for (i = 0; i < Infinity)`) y el único punto por el que pasan todas sus peticiones es su `fetch`.
import { fetchDeAuthQueNoInsiste } from './refrescoQueNoInsiste'

/** Lo que se hace al rendirse: borrar la sesión local y avisar a la app, que es lo que lleva a la pantalla de entrada. */
function alRendirseConElRefresco({ fallos, n }) {
  console.error(`[auth] el refresco de sesión falló ${fallos} veces seguidas (N=${n}): se borra la sesión local y se vuelve a la entrada`)
  try {
    // `scope: 'local'` a propósito: el borde no responde, así que pedirle que cierre la sesión en servidor sería otra petición al sitio que no contesta.
    if (supabase && supabase.auth && supabase.auth.signOut) supabase.auth.signOut({ scope: 'local' }).catch(() => {})
  } catch (e) { /* el camino de salida no puede fallar */ }
  try {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('pl:sesion-sin-refresco', { detail: { fallos, n } }))
    }
  } catch (e) { /* idem */ }
}

export const supabase = IS_SUPABASE_CONFIGURED
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        // COLA:el-refresco-no-insiste · DE VUELTA AL DEFECTO (5.000 ms). Estaba en 2.000 «para que la UI se desbloquee antes», y esa perilla fue mi
        // sospechoso del bucle del 7-oct — la reproducción lo **descartó**: con el service worker bloqueado la app hace UN intento y va a la entrada, con
        // el 504 tardando 1,5 s y tardando los 20 s reales. No era la causa, pero tampoco tenía ninguna medida detrás, y una perilla no estándar sin
        // medida es deuda: vuelve al defecto de la librería.
        lockAcquireTimeout: 5000,
      },
      global: { fetch: fetchDeAuthQueNoInsiste((...a) => fetch(...a), { alRendirse: alRendirseConElRefresco }) },
    })
  : createStub()

export default supabase
